import { useReactFlow } from '@xyflow/react';
import { css } from 'aphrodite';
import { useState } from 'react';
import { DEFAULT_CANVAS_FIT_VIEW_OPTIONS } from '../utils/viewport';
import { CanvasInfoPopover, InfoIcon } from './CanvasInfoPopover';
import { styleSheet } from './styles/CanvasControlsStyles';

export interface CanvasControlsProps {
	animateEdges: boolean;
	onToggleAnimateEdges: () => void;
	autoConnect: boolean;
	onToggleAutoConnect: () => void;
	onShowAllShortcuts: () => void;
	isMiniMapVisible: boolean;
	onToggleMiniMap: () => void;
	onRunAutoLayout: () => void;
}

export function CanvasControls({
	animateEdges,
	onToggleAnimateEdges,
	autoConnect,
	onToggleAutoConnect,
	onShowAllShortcuts,
	isMiniMapVisible,
	onToggleMiniMap,
	onRunAutoLayout,
}: CanvasControlsProps) {
	const { zoomIn, zoomOut, fitView } = useReactFlow();
	const [isInfoOpen, setIsInfoOpen] = useState(false);

	return (
		<div className={css(styleSheet.container)}>
			<button
				type='button'
				className={css(styleSheet.button)}
				onClick={() => zoomIn()}
				title='Zoom In'
				aria-label='Zoom In'
			>
				<ZoomInIcon />
			</button>
			<button
				type='button'
				className={css(styleSheet.button)}
				onClick={() => zoomOut()}
				title='Zoom Out'
				aria-label='Zoom Out'
			>
				<ZoomOutIcon />
			</button>
			<button
				type='button'
				className={css(styleSheet.button)}
				onClick={() => fitView(DEFAULT_CANVAS_FIT_VIEW_OPTIONS)}
				title='Fit View'
				aria-label='Fit View'
			>
				<FitViewIcon />
			</button>
			<button
				type='button'
				className={css(styleSheet.button)}
				onClick={onRunAutoLayout}
				title='Re-run Auto-Layout'
				aria-label='Re-run Auto-Layout'
			>
				<AutoLayoutIcon />
			</button>
			<button
				type='button'
				className={css(styleSheet.button, isMiniMapVisible && styleSheet.activeButton)}
				onClick={onToggleMiniMap}
				title={isMiniMapVisible ? 'Hide Minimap' : 'Show Minimap'}
				aria-pressed={isMiniMapVisible}
				aria-label={isMiniMapVisible ? 'Hide Minimap' : 'Show Minimap'}
			>
				<MiniMapIcon />
			</button>
			<div className={css(styleSheet.divider)} />
			<button
				type='button'
				className={css(styleSheet.button, animateEdges && styleSheet.activeButton)}
				onClick={onToggleAnimateEdges}
				title={animateEdges ? 'Disable Edge Animation' : 'Enable Edge Animation'}
				aria-pressed={animateEdges}
				aria-label={animateEdges ? 'Disable Edge Animation' : 'Enable Edge Animation'}
			>
				<EdgeAnimationIcon />
			</button>
			<button
				type='button'
				className={css(styleSheet.button, autoConnect && styleSheet.activeButton)}
				onClick={onToggleAutoConnect}
				title={autoConnect ? 'Disable Auto-Connect' : 'Enable Auto-Connect'}
				aria-pressed={autoConnect}
				aria-label={autoConnect ? 'Disable Auto-Connect' : 'Enable Auto-Connect'}
			>
				<AutoConnectIcon />
			</button>
			<div className={css(styleSheet.divider)} />
			<div className={css(styleSheet.infoWrapper)}>
				<button
					type='button'
					className={css(styleSheet.button, isInfoOpen && styleSheet.activeButton)}
					onClick={() => setIsInfoOpen(prev => !prev)}
					title='Canvas Info & Shortcuts'
					aria-expanded={isInfoOpen}
					aria-label='Canvas Info & Shortcuts'
				>
					<InfoIcon />
				</button>
				{isInfoOpen && (
					<CanvasInfoPopover onClose={() => setIsInfoOpen(false)} onShowAllShortcuts={onShowAllShortcuts} />
				)}
			</div>
		</div>
	);
}

function ZoomInIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path d='M8 3v10M3 8h10' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
		</svg>
	);
}

function ZoomOutIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path d='M3 8h10' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
		</svg>
	);
}

function FitViewIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<rect x='2' y='2' width='12' height='12' rx='2' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<path d='M6 8l2 2 2-2' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

function AutoLayoutIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<rect x='2' y='2' width='5' height='5' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<rect x='9' y='2' width='5' height='5' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<rect x='2' y='9' width='5' height='5' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<rect x='9' y='9' width='5' height='5' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<path d='M7 4.5h2' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
			<path d='M4.5 7v2' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
		</svg>
	);
}

function MiniMapIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<rect x='2' y='3' width='12' height='10' rx='2' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<rect x='4' y='5' width='4' height='3' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<rect x='9' y='9' width='3' height='2' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
		</svg>
	);
}

function EdgeAnimationIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path
				d='M2 8h3l2-4 2 8 2-4h3'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
		</svg>
	);
}

function AutoConnectIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<circle cx='4' cy='8' r='2' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<circle cx='12' cy='8' r='2' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<path d='M6 8h4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
		</svg>
	);
}
